/**
 * useVatPeriodForDate Hook
 * Retourne la période TVA correspondant à une date donnée
 * Sprint 5 - Liens Contextuels
 */

import { useState, useEffect, useMemo } from 'react';
import { vatPeriodsAPI } from '../services/tax.service';
import type { VatPeriod } from '../types/tax.types';

interface UseVatPeriodForDateResult {
  vatPeriod: VatPeriod | null;
  loading: boolean;
  error: string | null;
}

// Cache global pour éviter les appels API répétés
let cachedPeriods: VatPeriod[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Hook pour trouver la période TVA correspondant à une date
 * @param date - Date à rechercher (string ISO ou Date)
 * @returns { vatPeriod, loading, error }
 */
export function useVatPeriodForDate(date: string | Date | null | undefined): UseVatPeriodForDateResult {
  const [periods, setPeriods] = useState<VatPeriod[]>(cachedPeriods || []);
  const [loading, setLoading] = useState(!cachedPeriods);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const now = Date.now();

    // Use cache if still valid
    if (cachedPeriods && (now - cacheTimestamp) < CACHE_DURATION) {
      setPeriods(cachedPeriods);
      setLoading(false);
      return;
    }

    // Load periods
    const loadPeriods = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await vatPeriodsAPI.getAll();
        cachedPeriods = data;
        cacheTimestamp = now;
        setPeriods(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };

    loadPeriods();
  }, []);

  // Find matching period
  const vatPeriod = useMemo(() => {
    if (!date || periods.length === 0) return null;

    const targetDate = typeof date === 'string' ? new Date(date) : date;

    // Find period that contains the date
    return periods.find(period => {
      const startDate = new Date(period.startDate);
      const endDate = new Date(period.endDate);
      return targetDate >= startDate && targetDate <= endDate;
    }) || null;
  }, [date, periods]);

  return { vatPeriod, loading, error };
}

/**
 * Hook pour obtenir toutes les périodes TVA (avec cache)
 */
export function useVatPeriods() {
  const [periods, setPeriods] = useState<VatPeriod[]>(cachedPeriods || []);
  const [loading, setLoading] = useState(!cachedPeriods);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const now = Date.now();

    if (cachedPeriods && (now - cacheTimestamp) < CACHE_DURATION) {
      setPeriods(cachedPeriods);
      setLoading(false);
      return;
    }

    const loadPeriods = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await vatPeriodsAPI.getAll();
        cachedPeriods = data;
        cacheTimestamp = now;
        setPeriods(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };

    loadPeriods();
  }, []);

  return { periods, loading, error };
}

/**
 * Fonction utilitaire pour formater une période TVA en badge
 */
export function formatVatPeriodBadge(period: VatPeriod | null): string {
  if (!period) return '';
  return `TVA: ${period.name}`;
}

/**
 * Fonction utilitaire pour obtenir la couleur du badge selon le statut
 */
export function getVatPeriodBadgeColor(period: VatPeriod | null): string {
  if (!period) return 'bg-gray-100 text-gray-600';

  switch (period.status) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'READY':
      return 'bg-yellow-100 text-yellow-700';
    case 'DECLARED':
      return 'bg-blue-100 text-blue-700';
    case 'PAID':
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

/**
 * Invalider le cache (à appeler après création/modification de périodes)
 */
export function invalidateVatPeriodsCache(): void {
  cachedPeriods = null;
  cacheTimestamp = 0;
}

export default useVatPeriodForDate;
